import { Error, List, RecordContextProvider, useGetRecordId, useListContext } from "react-admin";
import { drivingSchoolEmployeeRoles, type Employee } from "../../providers/employeesProvider";
import { alpha, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { CardsGrid } from "../../misc/CardsGrid";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import { useNavigate } from "react-router";
import { grants } from "../../backoffice.access_control";
import { AccessDenied } from "../../misc/AccessDenied";
import intersection from "lodash/intersection";
import { assertNever } from "../../backoffice.utils";
import * as React from "react";
import { EmptyState } from "../../misc/EmptyState";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { OfficeEmployeeMobileListItem } from "../../lists/OfficeEmployeeMobileListItem";
import { SearchFilterInput } from "../../inputs/SearchFilterInput";
import { EmployeesListSkeleton } from "../../lists/EmployeesListSkeleton";
import { EmployeeAvatar } from "../../misc/EmployeeAvatar";

const employeeRoles = ["drivingSchoolCustomerService", "drivingSchoolAccountant"] as const;
type EmployeeRole = (typeof employeeRoles)[number];

export function DrivingSchoolOfficeEmployees(props: { showInactive?: boolean }) {
  const { showInactive = false } = props;
  const drivingSchoolId = useGetRecordId();

  if (!grants.has("Employee:list")) {
    return <AccessDenied />;
  }
  if (!drivingSchoolId) {
    return (
      <ResponsiveListSurface variant="compactNarrowTabContent" data-testid="DrivingSchoolOfficeEmployees">
        <EmployeesListSkeleton kind="office" variant="full" />
      </ResponsiveListSurface>
    );
  }

  const empty = (
    <EmptyState
      noEntriesText="Noch kein weiterer Mitarbeiter vorhanden."
      noMatchingEntriesText="Keine passenden Mitarbeiter gefunden."
    />
  );

  return (
    <ResponsiveListSurface variant="compactNarrowTabContent" data-testid="DrivingSchoolOfficeEmployees">
      <List
        className="cards-list"
        resource="employees"
        title=" " // <-- prevent that the default list title is rendered
        storeKey={`DrivingSchoolOfficeEmployees-${showInactive ? "inactive" : "active"}@${drivingSchoolId}`}
        filter={{
          drivingSchoolId,
          roles: (it: Array<Role>) =>
            intersection(drivingSchoolEmployeeRoles, it).length > 0 && !it.includes("instructor"),
          disabled: showInactive,
        }}
        filters={[<SearchFilterInput key="q" source="q" alwaysOn resettable />]}
        actions={false}
        sort={{ field: "_sortOrder", order: "ASC" }}
        perPage={9999}
        pagination={false}
        disableSyncWithLocation
        exporter={false}
        empty={empty}
        hasCreate={false}
        sx={{
          boxShadow: "none",
          marginLeft: "auto",
          "& .RaList-content": { boxShadow: "none", border: "none", borderRadius: 0 },
          "& .RaList-actions-topToolbar": {
            justifyContent: "flex-start",
          },
        }}
      >
        <_OfficeEmployeeCards empty={empty} />
      </List>
    </ResponsiveListSurface>
  );
}

function _OfficeEmployeeCards(props: { empty: React.ReactElement }) {
  const { empty } = props;
  const { data: supportEmployees, isLoading, error, refetch, isFetching } = useListContext<Employee>();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  if (error && !supportEmployees) {
    const retry = () => {
      void runSafely(async () => refetch({ throwOnError: true }), {
        errorMessage: "Die Mitarbeiter konnten nicht erneut geladen werden.",
      });
    };
    return (
      <>
        <Error error={error} resetErrorBoundary={retry} />
        <Row sx={{ justifyContent: "center", mt: 2 }}>
          <LoadingButton variant="outlined" startIcon={<RefreshIcon />} loading={isFetching} onClick={retry}>
            Erneut laden
          </LoadingButton>
        </Row>
      </>
    );
  }
  if (isLoading && !supportEmployees) {
    return <EmployeesListSkeleton kind="office" />;
  }
  if (!supportEmployees || supportEmployees.length === 0) {
    return empty;
  }
  if (isMobile) {
    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {supportEmployees.map((employee) => (
          <OfficeEmployeeMobileListItem
            key={employee.id}
            employee={employee}
            roleLabel={_formatEmployeeRoles(employee)}
            onClick={() => navigate(`/employees/${employee.id}`)}
          />
        ))}
      </Column>
    );
  }
  return (
    <CardsGrid sx={{ mt: "16px", rowGap: "24px", columnGap: "24px" }}>
      {supportEmployees.map((employee) => (
        <_EmployeeCard key={employee.id} employee={employee} />
      ))}
    </CardsGrid>
  );
}

function _EmployeeCard({ employee }: { employee: Employee }) {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <RecordContextProvider value={employee}>
      <CardsGrid.Card
        onClick={() => navigate(`/employees/${employee.id}`)}
        sx={{
          backgroundColor: overviewCardStyle.backgroundColor,
          border: "1px solid",
          borderColor: alpha(theme.palette.text.primary, 0.08),
          boxShadow: "none",
          "&:hover": { borderColor: "text.secondary", boxShadow: "none" },
          width: "100%",
          minWidth: "200px",
          ...(employee.disabled ? { "& .MuiTypography-root": { color: "text.disabled" } } : {}),
        }}
      >
        <Column sx={{ mt: "28px", height: "192px", alignItems: "center" }} gap="8px">
          <EmployeeAvatar src={employee.avatarUrl} sx={{ position: "relative", top: -2, width: 60, height: 60 }} />
          <_Name employee={employee} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {_formatEmployeeRoles(employee)}
          </Typography>
        </Column>
      </CardsGrid.Card>
    </RecordContextProvider>
  );
}

function _Name({ employee }: { employee: Employee }) {
  return (
    <Typography
      sx={{
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "20px",
        textAlign: "center",
      }}
    >
      {employee.firstName || "\u00A0"}
      <br />
      {employee.lastName || "\u00A0"}
    </Typography>
  );
}

function _formatEmployeeRoles(employee: Employee): string {
  return intersection(employee.roles, employeeRoles)
    .map((role) => getEmployeeRoleLabel(role as EmployeeRole))
    .join(", ");
}

function getEmployeeRoleLabel(employeeRole: EmployeeRole): string {
  switch (employeeRole) {
    case "drivingSchoolCustomerService":
      return "Kundenservice";
    case "drivingSchoolAccountant":
      return "Buchhaltung";
    default:
      return assertNever(employeeRole);
  }
}
